import type { SystemType, PendulumParams, SimSettings, PhaseSpaceDimension, SimulationConfig } from './types.ts';
import {
  SYSTEM_NAMES, DEFAULT_PARAMS, DEFAULT_SIM, DEFAULT_INITIAL_STATE,
  systemDimensions, DIM_ORDER,
} from './types.ts';

export interface UIValues {
  config: SimulationConfig;
  playing: boolean;
  reset: boolean;
  clearTrail: boolean;
}

type ChangeCallback = (values: UIValues) => void;

export interface UIHandle {
  /** Write a full config into the on-screen controls (no side effects beyond the DOM). */
  applyConfigToControls(config: SimulationConfig): void;
  /** Re-read the controls and push them through as a reset (re-creates the sim). */
  triggerReset(): void;
}

export function createUI(onChange: ChangeCallback): UIHandle {
  const systemSelect = document.getElementById('systemSelect') as HTMLSelectElement;
  const panels: Record<string, HTMLElement> = {
    doublePendulum: document.getElementById('doublePendulumParams')!,
    sculpture: document.getElementById('sculptureParams')!,
    resonant: document.getElementById('resonantParams')!,
  };

  const sliderIds: Record<string, string> = {
    m1: 'slider_m1', m2: 'slider_m2', L1: 'slider_L1', L2: 'slider_L2',
    k1: 'slider_k1', k2: 'slider_k2',
    sculptureWeight: 'slider_scWeight', sculptureRod: 'slider_scRod',
    sculptureAxle: 'slider_scAxle', sculptureReduction: 'slider_scReduction',
    sculptureN: 'slider_scN',
    rpM0: 'slider_rpM0', rpM1: 'slider_rpM1', rpL0: 'slider_rpL0',
    rpL1: 'slider_rpL1', rpA0: 'slider_rpA0',
    dt: 'slider_dt', stepsPerFrame: 'slider_stepsPerFrame',
  };

  const sliders: Record<string, HTMLInputElement> = {};
  for (const [key, id] of Object.entries(sliderIds)) {
    sliders[key] = document.getElementById(id) as HTMLInputElement;
  }

  const displays: Record<string, HTMLSpanElement> = {};
  for (const key of Object.keys(sliders)) {
    displays[key] = document.getElementById(`val_${key}`) as HTMLSpanElement;
  }

  const initialInputs: Record<string, HTMLInputElement> = {};
  for (const dim of DIM_ORDER) {
    initialInputs[dim] = document.getElementById(`init_${dim}`) as HTMLInputElement;
  }

  const playBtn = document.getElementById('playPauseBtn')!;
  const resetBtn = document.getElementById('resetBtn')!;
  const trailBtn = document.getElementById('clearTrailBtn')!;

  let playing = false;

  function readParams(): PendulumParams {
    return {
      m1: parseFloat(sliders.m1.value),
      m2: parseFloat(sliders.m2.value),
      L1: parseFloat(sliders.L1.value),
      L2: parseFloat(sliders.L2.value),
      k1: parseFloat(sliders.k1.value),
      k2: parseFloat(sliders.k2.value),
      sculptureWeight: parseFloat(sliders.sculptureWeight.value),
      sculptureRod: parseFloat(sliders.sculptureRod.value),
      sculptureAxle: parseFloat(sliders.sculptureAxle.value),
      sculptureReduction: parseFloat(sliders.sculptureReduction.value),
      sculptureN: parseInt(sliders.sculptureN.value),
      rpM0: parseFloat(sliders.rpM0.value),
      rpM1: parseFloat(sliders.rpM1.value),
      rpL0: parseFloat(sliders.rpL0.value),
      rpL1: parseFloat(sliders.rpL1.value),
      rpA0: parseFloat(sliders.rpA0.value),
    };
  }

  function readSim(): SimSettings {
    return {
      dt: parseFloat(sliders.dt.value),
      stepsPerFrame: parseInt(sliders.stepsPerFrame.value),
    };
  }

  function readInitial(): Record<PhaseSpaceDimension, number> {
    const out = { ...DEFAULT_INITIAL_STATE };
    for (const dim of DIM_ORDER) {
      const v = parseFloat(initialInputs[dim]?.value ?? '0');
      if (!isNaN(v)) out[dim] = v;
    }
    return out;
  }

  function updateDisplays() {
    for (const key of Object.keys(sliders)) {
      if (displays[key]) displays[key].textContent = sliders[key].value;
    }
  }

  function updateParamVisibility(system: SystemType) {
    const hideAll = () => {
      for (const p of Object.values(panels)) p.style.display = 'none';
    };
    hideAll();
    if (system === 'rigid' || system === 'elastic12' || system === 'nonlinear') {
      panels.doublePendulum.style.display = 'block';
      sliders.k1.parentElement!.style.display = (system === 'elastic12' || system === 'nonlinear') ? 'block' : 'none';
      sliders.k2.parentElement!.style.display = (system === 'elastic12' || system === 'nonlinear') ? 'block' : 'none';
    } else if (system === 'sculpture') {
      panels.sculpture.style.display = 'block';
    } else if (system === 'resonantPendulum') {
      panels.resonant.style.display = 'block';
    }

    const dims = systemDimensions(system, parseInt(sliders.sculptureN.value));
    const dimToRow: Record<string, HTMLElement> = {};
    for (const dim of DIM_ORDER) {
      const el = document.getElementById(`initRow_${dim}`);
      if (el) dimToRow[dim] = el;
    }
    for (const dim of DIM_ORDER) {
      if (dimToRow[dim]) {
        dimToRow[dim].style.display = dims.includes(dim) ? 'flex' : 'none';
      }
    }
  }

  function emit(opts?: { reset?: boolean; clearTrail?: boolean }) {
    updateDisplays();
    const system = systemSelect.value as SystemType;
    updateParamVisibility(system);
    onChange({
      config: {
        system,
        params: readParams(),
        sim: readSim(),
        initialState: readInitial(),
      },
      playing,
      reset: opts?.reset ?? false,
      clearTrail: opts?.clearTrail ?? false,
    });
  }

  systemSelect.addEventListener('change', () => emit());
  for (const s of Object.values(sliders)) {
    s.addEventListener('input', () => emit());
  }
  for (const inp of Object.values(initialInputs)) {
    inp.addEventListener('change', () => emit());
  }

  playBtn.addEventListener('click', () => {
    playing = !playing;
    playBtn.textContent = playing ? '\u23F8 Pause' : '\u25B6 Play';
    emit();
  });

  resetBtn.addEventListener('click', () => {
    playing = false;
    playBtn.textContent = '\u25B6 Play';
    emit({ reset: true });
  });

  trailBtn.addEventListener('click', () => {
    emit({ clearTrail: true });
  });

  function applyConfigToControls(config: SimulationConfig): void {
    systemSelect.value = config.system;
    for (const [key, slider] of Object.entries(sliders)) {
      let v: number | undefined;
      if (key === 'dt' || key === 'stepsPerFrame') {
        v = config.sim[key];
      } else {
        v = config.params[key as keyof PendulumParams];
      }
      if (typeof v === 'number') slider.value = String(v);
    }
    for (const dim of DIM_ORDER) {
      const inp = initialInputs[dim];
      if (inp) inp.value = String(config.initialState[dim]);
    }
    updateDisplays();
    updateParamVisibility(config.system);
  }

  function triggerReset(): void {
    emit({ reset: true });
  }

  emit();

  return { applyConfigToControls, triggerReset };
}

export function resetInitialInputs(system: SystemType, sculptureN: number): void {
  const dims = systemDimensions(system, sculptureN);
  for (const dim of DIM_ORDER) {
    const inp = document.getElementById(`init_${dim}`) as HTMLInputElement | null;
    if (inp) {
      inp.value = dims.includes(dim) ? '0' : '0';
    }
  }
}
